import { captureException } from '@sentry/nextjs';

type NaverWcsCommand = () => void;

const READY_POLL_INTERVAL_MS = 300;
const READY_POLL_TIMEOUT_MS = 30_000;

const pendingCommands: NaverWcsCommand[] = [];
let readyPollTimerId: ReturnType<typeof setInterval> | null = null;

const isWcsReady = () =>
  typeof window !== 'undefined' && typeof window.wcs?.trans === 'function';

const runCommand = (command: NaverWcsCommand) => {
  try {
    command();
  } catch (error) {
    captureException(error);
  }
};

const stopReadyPoll = () => {
  if (readyPollTimerId === null) {
    return;
  }

  clearInterval(readyPollTimerId);
  readyPollTimerId = null;
};

/**
 * 준비 신호를 wcslog.js의 `onLoad` 하나에만 의존하면, 그 콜백이 오지 않는 경로에서
 * 큐가 영구히 남는다(next/script는 이미 로드된 스크립트의 재마운트에 onLoad를 다시 주지 않는다).
 * 실제 `window.wcs` 실존을 폴링해 신호원을 이중화한다.
 */
const startReadyPoll = () => {
  if (readyPollTimerId !== null) {
    return;
  }

  const startedAt = Date.now();

  readyPollTimerId = setInterval(() => {
    if (isWcsReady()) {
      flushNaverWcsCommands();

      return;
    }

    if (Date.now() - startedAt >= READY_POLL_TIMEOUT_MS) {
      stopReadyPoll();
      pendingCommands.length = 0;
    }
  }, READY_POLL_INTERVAL_MS);
};

/**
 * wcs 준비 전 호출은 적재되고, 준비되는 즉시 순서대로 실행된다 — gtag dataLayer와 같은 패턴.
 * 반환된 cleanup으로 미실행 커맨드를 취소할 수 있다.
 */
export const runOnNaverWcsReady = (command: NaverWcsCommand) => {
  if (isWcsReady()) {
    runCommand(command);

    return () => {};
  }

  pendingCommands.push(command);
  startReadyPoll();

  return () => {
    const commandIndex = pendingCommands.indexOf(command);

    if (commandIndex !== -1) {
      pendingCommands.splice(commandIndex, 1);
    }
  };
};

/**
 * 선언형 트리거를 걸 수 없는 지점(mutation onSuccess 등)에서 전환을 쏜다.
 * 렌더 트리에 붙일 수 있으면 `NaverCtsTransTrigger`를 쓴다.
 */
export const trackNaverCtsConversion = (
  transOption: Parameters<Window['wcs']['trans']>[0],
) =>
  runOnNaverWcsReady(() => {
    window.wcs.trans(transOption);
  });

export const flushNaverWcsCommands = () => {
  if (!isWcsReady()) {
    startReadyPoll();

    return;
  }

  stopReadyPoll();

  while (pendingCommands.length > 0) {
    const command = pendingCommands.shift();

    if (command) {
      runCommand(command);
    }
  }
};
